import {
  repairAnnotationRanges,
  type AnnotationRange,
} from "./annotations.js";
import {
  withOrderNormalizationReport,
  type AnnotationRepairReport,
} from "./annotation-repair-tray.js";
import {
  CHOREOGRAPHY_DEFINITIONS,
  THEME_DEFINITIONS,
} from "./atlas.js";
import { EDITOR_MODES, type EditorMode } from "./pack.js";

export const SESSION_STORAGE_KEY = "undef-haiku:north-star:session";
export const SESSION_RECOVERY_PREFIX = `${SESSION_STORAGE_KEY}:recovery:`;

const SESSION_KEYS = Object.freeze([
  "schemaVersion",
  "packRef",
  "draft",
  "editorMode",
  "themeId",
  "choreographyId",
  "annotations",
  "games",
  "updatedAt",
] as const);

const CONTENT_KEYS = Object.freeze([
  "packRef",
  "draft",
  "editorMode",
  "themeId",
  "choreographyId",
  "annotations",
  "games",
] as const);

const MAX_RECOVERY_KEY_PROBES = 1_000;

export type SessionJsonValue =
  | null
  | boolean
  | number
  | string
  | readonly SessionJsonValue[]
  | SessionJsonObject;

export interface SessionJsonObject {
  readonly [key: string]: SessionJsonValue;
}

export interface NorthStarSessionContents {
  readonly packRef: string;
  readonly draft: string;
  readonly editorMode: EditorMode;
  readonly themeId: string;
  readonly choreographyId: string;
  readonly annotations: readonly AnnotationRange[];
  readonly games: SessionJsonObject;
}

export interface NorthStarSessionRecord extends NorthStarSessionContents {
  readonly schemaVersion: 1;
  readonly updatedAt: number;
}

export interface SessionStorageBoundary {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type SessionClock = () => number;

export interface SessionCommitResult {
  readonly status: "committed" | "memory-only";
  readonly persisted: boolean;
  readonly error?: unknown;
}

export interface SessionClockErrorResult {
  readonly status: "clock-error";
  readonly persisted: false;
  readonly error: unknown;
}

export type SessionSaveResult = SessionCommitResult | SessionClockErrorResult;

export interface SessionStaleResult {
  readonly status: "stale";
  readonly persisted: false;
}

export interface SessionDiscardResult {
  readonly status: "discarded";
  readonly persisted: false;
}

export type SessionDecisionResult =
  | SessionCommitResult
  | SessionStaleResult
  | SessionDiscardResult;

export interface SessionRecoveryResult {
  readonly status: "recovered" | "storage-error" | "clock-error";
  readonly persisted: boolean;
  readonly key?: string;
  readonly error?: unknown;
}

export interface SessionLoadStatus {
  readonly status:
    | "loaded"
    | "fallback-empty"
    | "storage-unavailable"
    | "invalid-recovered"
    | "invalid-unrecovered";
  readonly error?: unknown;
  readonly recovery?: SessionRecoveryResult;
}

export interface ImportedSessionResult {
  readonly status: "imported";
  readonly result: SessionCommitResult;
}

export interface InvalidSessionImportResult {
  readonly status: "invalid";
  readonly error: string;
  readonly recovery: SessionRecoveryResult;
}

export interface PendingSessionImportResult {
  readonly status: "repair-pending";
  readonly repair: AnnotationRepairReport;
  commitRepaired(): SessionDecisionResult;
  discardAffected(): SessionDecisionResult;
  discard(): SessionDecisionResult;
}

export type SessionImportResult =
  | ImportedSessionResult
  | InvalidSessionImportResult
  | PendingSessionImportResult;

export interface NorthStarSession {
  readonly state: NorthStarSessionRecord;
  readonly loadStatus: SessionLoadStatus;
  save(contents: NorthStarSessionContents): SessionSaveResult;
  exportJSON(): string;
  importJSON(raw: string): SessionImportResult;
}

export interface CreateNorthStarSessionOptions {
  readonly initial: NorthStarSessionRecord;
  readonly storage: SessionStorageBoundary;
  readonly clock: SessionClock;
}

interface ParsedSessionRecord {
  readonly record: NorthStarSessionRecord;
  readonly repair: AnnotationRepairReport;
}

type ThemeId = (typeof THEME_DEFINITIONS)[number]["id"];
type ChoreographyId = (typeof CHOREOGRAPHY_DEFINITIONS)[number]["id"];

export interface EditorResumability {
  readonly lines: readonly [string, string, string];
  readonly themeId: ThemeId;
  readonly choreographyId: ChoreographyId;
}

export const validateEditorResumability = (
  lines: unknown,
  themeId: unknown,
  choreographyId: unknown,
): EditorResumability => {
  if (!Array.isArray(lines)) {
    throw new TypeError("editor requires exactly three string lines");
  }
  let canonicalLines: [string, string, string];
  try {
    if (Object.getPrototypeOf(lines) !== Array.prototype) {
      throw new TypeError("editor lines must be a plain array");
    }
    const keys = Reflect.ownKeys(lines);
    if (keys.length !== 4
        || !["0", "1", "2", "length"].every((key) => keys.includes(key))) {
      throw new TypeError("editor requires a dense three-line array");
    }
    const lengthDescriptor = Object.getOwnPropertyDescriptor(lines, "length");
    if (lengthDescriptor === undefined || !("value" in lengthDescriptor)
        || lengthDescriptor.value !== 3 || lengthDescriptor.enumerable) {
      throw new TypeError("editor requires exactly three string lines");
    }
    const values: string[] = [];
    for (let index = 0; index < 3; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(lines, String(index));
      if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
        throw new TypeError(`editor line ${index + 1} must be a data property`);
      }
      const value: unknown = descriptor.value;
      if (typeof value !== "string" || /[\r\n]/u.test(value)) {
        throw new TypeError("editor lines must be canonical single strings");
      }
      values.push(value);
    }
    globalThis.structuredClone(lines);
    canonicalLines = values as [string, string, string];
  } catch (error) {
    if (error instanceof TypeError) throw error;
    throw new TypeError("editor lines must be a safe plain data array", { cause: error });
  }
  if (typeof themeId !== "string" || !THEME_DEFINITIONS.some(({ id }) => id === themeId)) {
    throw new RangeError("editor theme is not in the atlas");
  }
  if (typeof choreographyId !== "string"
      || !CHOREOGRAPHY_DEFINITIONS.some(({ id }) => id === choreographyId)) {
    throw new RangeError("editor choreography is not in the atlas");
  }
  const frozenLines = Object.freeze([
    canonicalLines[0],
    canonicalLines[1],
    canonicalLines[2],
  ] as [string, string, string]);
  return Object.freeze({
    lines: frozenLines,
    themeId: themeId as ThemeId,
    choreographyId: choreographyId as ChoreographyId,
  });
};

const readExactRecord = (
  value: unknown,
  keys: readonly string[],
  label: string,
): readonly unknown[] => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be a plain object`);
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${label} must be a plain object`);
  }

  const actualKeys = Reflect.ownKeys(value);
  if (
    actualKeys.length !== keys.length
    || actualKeys.some((key) => typeof key !== "string" || !keys.includes(key))
  ) {
    throw new TypeError(`${label} contains unknown or missing keys`);
  }

  return keys.map((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}.${key} must be an enumerable data property`);
    }
    return descriptor.value as unknown;
  });
};

const readArrayValues = (value: unknown, label: string): readonly unknown[] => {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
    throw new TypeError(`${label} must be an array`);
  }
  const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
  if (
    lengthDescriptor === undefined
    || lengthDescriptor.enumerable
    || !("value" in lengthDescriptor)
    || !Number.isSafeInteger(lengthDescriptor.value)
    || lengthDescriptor.value < 0
  ) {
    throw new TypeError(`${label}.length must be a non-negative integer data property`);
  }
  const length = lengthDescriptor.value as number;
  const actualKeys = Reflect.ownKeys(value);
  if (actualKeys.length !== length + 1) {
    throw new TypeError(`${label} must be a dense array without extra properties`);
  }

  const values: unknown[] = [];
  for (let index = 0; index < length; index += 1) {
    const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}[${index}] must be an enumerable data property`);
    }
    values.push(descriptor.value as unknown);
  }
  return values;
};

const readText = (value: unknown, label: string, allowEmpty = false): string => {
  if (typeof value !== "string" || (!allowEmpty && value.length === 0)) {
    throw new TypeError(`${label} must be ${allowEmpty ? "a string" : "a non-empty string"}`);
  }
  return value;
};

const readFiniteNumber = (value: unknown, label: string): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${label} must be a finite number`);
  }
  return value;
};

const readTimestamp = (value: unknown, label: string): number => {
  const timestamp = readFiniteNumber(value, label);
  if (!Number.isInteger(timestamp) || timestamp < 0) {
    throw new RangeError(`${label} must be a non-negative integer`);
  }
  return timestamp;
};

const readEditorMode = (value: unknown): EditorMode => {
  if (typeof value !== "string" || !(EDITOR_MODES as readonly string[]).includes(value)) {
    throw new TypeError("session.editorMode is unknown");
  }
  return value as EditorMode;
};

const readAnnotationRanges = (value: unknown): readonly AnnotationRange[] => Object.freeze(
  readArrayValues(value, "session.annotations").map((entry, index) => {
    const [id, start, end] = readExactRecord(
      entry,
      ["id", "start", "end"],
      `session.annotations[${index}]`,
    );
    return Object.freeze({
      id: readText(id, `session.annotations[${index}].id`),
      start: readFiniteNumber(start, `session.annotations[${index}].start`),
      end: readFiniteNumber(end, `session.annotations[${index}].end`),
    });
  }),
);

const cloneJsonValue = (
  value: unknown,
  label: string,
  ancestors: Set<object>,
): SessionJsonValue => {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") return readFiniteNumber(value, label);
  if (typeof value !== "object") throw new TypeError(`${label} must contain only JSON values`);
  if (ancestors.has(value)) throw new TypeError(`${label} must not contain cycles`);
  ancestors.add(value);

  try {
    if (Array.isArray(value)) {
      const cloned = readArrayValues(value, label).map((entry, index) =>
        cloneJsonValue(entry, `${label}[${index}]`, ancestors)
      );
      return Object.freeze(cloned);
    }

    const prototype: unknown = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      throw new TypeError(`${label} must contain only plain JSON objects`);
    }
    const clone = Object.create(null) as Record<string, SessionJsonValue>;
    for (const key of Reflect.ownKeys(value)) {
      if (typeof key !== "string") throw new TypeError(`${label} must not contain symbol keys`);
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
        throw new TypeError(`${label}.${key} must be an enumerable data property`);
      }
      clone[key] = cloneJsonValue(descriptor.value as unknown, `${label}.${key}`, ancestors);
    }
    return Object.freeze(clone);
  } finally {
    ancestors.delete(value);
  }
};

const readGames = (value: unknown): SessionJsonObject => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError("session.games must be a plain JSON object");
  }
  return cloneJsonValue(value, "session.games", new Set()) as SessionJsonObject;
};

const parseSessionValues = (
  values: readonly unknown[],
  allowAnnotationRepairs: boolean,
): ParsedSessionRecord => {
  const [
    schemaVersion,
    packRef,
    draft,
    editorMode,
    themeId,
    choreographyId,
    annotations,
    games,
    updatedAt,
  ] = values;

  if (schemaVersion !== 1) throw new TypeError("session.schemaVersion must be 1");
  const canonicalDraft = readText(draft, "session.draft", true);
  const resumability = validateEditorResumability(
    canonicalDraft.split("\n"),
    themeId,
    choreographyId,
  );
  const importedRanges = readAnnotationRanges(annotations);
  const deterministicRepair = repairAnnotationRanges(canonicalDraft, importedRanges);
  const repair = withOrderNormalizationReport(importedRanges, deterministicRepair);
  if (deterministicRepair.repairs.length > 0 && !allowAnnotationRepairs) {
    throw new RangeError("session.annotations require repair");
  }
  if (repair.repairs.length > deterministicRepair.repairs.length && !allowAnnotationRepairs) {
    throw new RangeError("session.annotations must already be in canonical order");
  }

  return Object.freeze({
    record: Object.freeze({
      schemaVersion: 1,
      packRef: readText(packRef, "session.packRef"),
      draft: canonicalDraft,
      editorMode: readEditorMode(editorMode),
      themeId: resumability.themeId,
      choreographyId: resumability.choreographyId,
      annotations: deterministicRepair.accepted,
      games: readGames(games),
      updatedAt: readTimestamp(updatedAt, "session.updatedAt"),
    }),
    repair,
  });
};

const parseSessionRecord = (
  value: unknown,
  allowAnnotationRepairs: boolean,
): ParsedSessionRecord => parseSessionValues(
  readExactRecord(value, SESSION_KEYS, "session"),
  allowAnnotationRepairs,
);

export const validateSessionRecord = (value: unknown): NorthStarSessionRecord =>
  parseSessionRecord(value, false).record;

const validateSessionContents = (value: unknown): NorthStarSessionRecord => {
  const values = readExactRecord(value, CONTENT_KEYS, "session contents");
  return parseSessionValues([1, ...values, 0], false).record;
};

const withTimestamp = (
  record: NorthStarSessionRecord,
  updatedAt: number,
): NorthStarSessionRecord => Object.freeze({
  schemaVersion: 1,
  packRef: record.packRef,
  draft: record.draft,
  editorMode: record.editorMode,
  themeId: record.themeId,
  choreographyId: record.choreographyId,
  annotations: record.annotations,
  games: record.games,
  updatedAt,
});

const withAnnotations = (
  record: NorthStarSessionRecord,
  annotations: readonly AnnotationRange[],
): NorthStarSessionRecord => validateSessionRecord({
  schemaVersion: 1,
  packRef: record.packRef,
  draft: record.draft,
  editorMode: record.editorMode,
  themeId: record.themeId,
  choreographyId: record.choreographyId,
  annotations,
  games: record.games,
  updatedAt: record.updatedAt,
});

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const clockTimestamp = (clock: SessionClock): number =>
  readTimestamp(clock(), "clock result");

export const createNorthStarSession = (
  options: CreateNorthStarSessionOptions,
): NorthStarSession => {
  const { storage, clock } = options;
  let current = validateSessionRecord(options.initial);

  const recover = (raw: string): SessionRecoveryResult => {
    let baseKey: string;
    try {
      baseKey = `${SESSION_RECOVERY_PREFIX}${clockTimestamp(clock)}`;
    } catch (error) {
      return Object.freeze({ status: "clock-error", persisted: false, error });
    }

    for (let suffix = 0; suffix < MAX_RECOVERY_KEY_PROBES; suffix += 1) {
      const key = suffix === 0 ? baseKey : `${baseKey}:${suffix}`;
      try {
        if (storage.getItem(key) !== null) continue;
      } catch (error) {
        return Object.freeze({ status: "storage-error", persisted: false, key, error });
      }

      try {
        storage.setItem(key, raw);
        return Object.freeze({ status: "recovered", persisted: true, key });
      } catch (error) {
        return Object.freeze({ status: "storage-error", persisted: false, key, error });
      }
    }

    const error = new Error("recovery key probe limit exceeded");
    return Object.freeze({ status: "storage-error", persisted: false, error });
  };

  let loadStatus: SessionLoadStatus = Object.freeze({ status: "fallback-empty" });
  let storedRaw: string | null;
  let storageReadSucceeded = false;
  try {
    storedRaw = storage.getItem(SESSION_STORAGE_KEY);
    storageReadSucceeded = true;
  } catch (error) {
    storedRaw = null;
    loadStatus = Object.freeze({ status: "storage-unavailable", error });
  }

  if (storageReadSucceeded && storedRaw !== null) {
      try {
        current = validateSessionRecord(JSON.parse(storedRaw) as unknown);
        loadStatus = Object.freeze({ status: "loaded" });
      } catch (error) {
        const recovery = recover(storedRaw);
        loadStatus = Object.freeze({
          status: recovery.persisted ? "invalid-recovered" : "invalid-unrecovered",
          error,
          recovery,
        });
      }
  }

  const commit = (candidate: NorthStarSessionRecord): SessionCommitResult => {
    const raw = JSON.stringify(candidate);
    try {
      storage.setItem(SESSION_STORAGE_KEY, raw);
      current = candidate;
      return Object.freeze({ status: "committed", persisted: true });
    } catch (error) {
      current = candidate;
      return Object.freeze({ status: "memory-only", persisted: false, error });
    }
  };

  const save = (contents: NorthStarSessionContents): SessionSaveResult => {
    const validated = validateSessionContents(contents);
    let updatedAt: number;
    try {
      updatedAt = clockTimestamp(clock);
    } catch (error) {
      return Object.freeze({ status: "clock-error", persisted: false, error });
    }
    return commit(withTimestamp(validated, updatedAt));
  };

  const invalidImport = (raw: string, error: unknown): InvalidSessionImportResult =>
    Object.freeze({ status: "invalid", error: errorMessage(error), recovery: recover(raw) });

  const pendingImport = (
    candidate: NorthStarSessionRecord,
    repair: AnnotationRepairReport,
  ): PendingSessionImportResult => {
    const baseState = current;
    const affectedIds = new Set(repair.repairs.map((entry) => entry.original.id));
    const retained = repair.accepted.filter((range) => !affectedIds.has(range.id));
    const withoutAffected = withAnnotations(candidate, retained);
    let decision: SessionDecisionResult | undefined;

    const decideCommit = (chosen: NorthStarSessionRecord): SessionDecisionResult => {
      if (decision !== undefined) return decision;
      if (current !== baseState) {
        decision = Object.freeze({ status: "stale", persisted: false });
        return decision;
      }
      decision = commit(chosen);
      return decision;
    };

    return Object.freeze({
      status: "repair-pending",
      repair,
      commitRepaired: (): SessionDecisionResult => decideCommit(candidate),
      discardAffected: (): SessionDecisionResult => decideCommit(withoutAffected),
      discard: (): SessionDecisionResult => {
        if (decision === undefined) {
          decision = Object.freeze({ status: "discarded", persisted: false });
        }
        return decision;
      },
    });
  };

  const importJSON = (raw: string): SessionImportResult => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch (error) {
      return invalidImport(raw, error);
    }

    let imported: ParsedSessionRecord;
    try {
      imported = parseSessionRecord(parsed, true);
    } catch (error) {
      return invalidImport(raw, error);
    }

    if (imported.repair.repairs.length > 0) {
      return pendingImport(imported.record, imported.repair);
    }
    return Object.freeze({ status: "imported", result: commit(imported.record) });
  };

  return Object.freeze({
    get state(): NorthStarSessionRecord {
      return current;
    },
    loadStatus,
    save,
    exportJSON: (): string => JSON.stringify(current),
    importJSON,
  });
};
